<script setup lang="ts">
import BaseInputText from '../../../../base/BaseInputText.vue'
import BaseInputCombobox from '../../../../base/BaseInputCombobox.vue'
import { getSelectableCountryCodes } from '../../../../../data'
import { GlobeIcon, LocationMarkerIcon } from '@heroicons/vue/solid'
import { onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'
import { HomeIcon } from '@heroicons/vue/outline'
import BaseInputCheckbox from '../../../../base/BaseInputCheckbox.vue'
import BaseHeader from '../../../../base/BaseHeader.vue'
import { useForm } from 'vee-validate'
import { getShowPostalAddressFields } from '../../../../../utilities/account/useAccountDataPersonal'
import { useLegalAccountFormState } from '../../../../../utilities/account/useAccountFormLegal'
import { useVModel } from '@vueuse/core'
import { LegalPersonAddress } from './forms'

const postalAddressEnabled = getShowPostalAddressFields()
const isPostalAddressSameAsPhysicalAddress = ref(false)

const form = useForm({
    validationSchema: {
        address: 'required',
        postal_code: 'required',
        city: 'required',
        country: 'required',
        postal_address: 'required',
        postal_address_postal_code: 'required',
        postal_address_city: 'required',
        postal_address_country: 'required',
    },
})

const props = defineProps<{
    modelValue: LegalPersonAddress
}>()
const emit = defineEmits(['update:modelValue'])

const formValues = useVModel(props, 'modelValue', emit)

watch(() => {
    if (isPostalAddressSameAsPhysicalAddress.value) {
        formValues.value.postal_address = formValues.value.address
        formValues.value.postal_address_postal_code = formValues.value.postal_code
        formValues.value.postal_address_city = formValues.value.city
        formValues.value.postal_address_country = formValues.value.country
    }
})

watchEffect(() => {
    if (
        formValues.value.postal_address === formValues.value.address &&
        formValues.value.postal_address_postal_code === formValues.value.postal_code &&
        formValues.value.postal_address_city === formValues.value.city &&
        formValues.value.postal_address_country === formValues.value.country &&
        formValues.value.address &&
        formValues.value.postal_code &&
        formValues.value.city &&
        formValues.value.country
    ) {
        isPostalAddressSameAsPhysicalAddress.value = true
    }
})

const formState = useLegalAccountFormState()

onMounted(() => {
    // @ts-ignore
    formState.currentForm = form
})

onUnmounted(() => {
    // @ts-ignore
    formState.currentForm = null
})
</script>

<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="formValues.address"
                :icon="LocationMarkerIcon"
                name="address"
                required
                :label="$t('account.address')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="formValues.postal_code"
                :icon="LocationMarkerIcon"
                required
                name="postal_code"
                :label="$t('account.postal_code')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="formValues.city"
                :icon="LocationMarkerIcon"
                required
                name="city"
                :label="$t('account.city')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCombobox
                v-model="formValues.country"
                :icon="GlobeIcon"
                required
                name="country"
                :options="getSelectableCountryCodes()"
                nullable
                :label="$t('account.country')" />
        </div>
    </div>

    <div v-if="postalAddressEnabled">
        <BaseHeader
            class="border-t border-gray-200 px-0 sm:px-0"
            :title="$t('legal_account_manager_beneficial_owner.postal_address')"
            :icon="HomeIcon">
            <BaseInputCheckbox
                v-model="isPostalAddressSameAsPhysicalAddress"
                class="mt-2"
                name="isPostalAddressSameAsPhysicalAddress"
                :label="$t('account.same_as_residential')" />
        </BaseHeader>
        <div class="grid grid-cols-1 gap-x-4 gap-y-8 py-5 sm:grid-cols-2">
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="formValues.postal_address"
                    :disabled="isPostalAddressSameAsPhysicalAddress"
                    :icon="LocationMarkerIcon"
                    required
                    name="postal_address"
                    :label="$t('account.address')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="formValues.postal_address_postal_code"
                    :disabled="isPostalAddressSameAsPhysicalAddress"
                    :icon="LocationMarkerIcon"
                    required
                    name="postal_address_postal_code"
                    :label="$t('account.postal_code')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="formValues.postal_address_city"
                    :disabled="isPostalAddressSameAsPhysicalAddress"
                    required
                    :icon="LocationMarkerIcon"
                    name="postal_address_city"
                    :label="$t('account.city')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputCombobox
                    v-model="formValues.postal_address_country"
                    :disabled="isPostalAddressSameAsPhysicalAddress"
                    required
                    :icon="GlobeIcon"
                    name="postal_address_country"
                    :options="getSelectableCountryCodes()"
                    nullable
                    :label="$t('account.country')" />
            </div>
        </div>
    </div>
</template>
